import { Suspense, useState } from "react";
import { IoClose } from "react-icons/io5";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import styled from "styled-components";

import useBrands from "../../../../clients/brands/useBrands";
import useBrandsOfUser from "../../../../clients/users/useBrandsOfUser";
import * as UI from "../../../../modules/ui-kit";
import {
    confirmUnsavedChanges,
    type UnsavedChangesContext,
} from "../../../../modules/unsaved-changes";

import type { PriceListAppOutletContext } from "../../index";

const Styled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    transition: opacity 200ms;

    ${UI.HeroInput} {
        font-size: 2.4em;
    }

    ol {
        display: flex;
        flex-direction: column;
        gap: 8px;
        list-style: none;
        margin: 0;
        padding: 0;

        a {
            color: #000;
            font-size: 1.2rem;
            overflow: hidden;
            position: relative;
            text-decoration: none;
            text-overflow: ellipsis;
            white-space: nowrap;

            ${UI.FluidAccent2}

            &:hover {
                text-decoration: none;
            }
        }
    }
`;

export default function Brands() {
    const [token, setToken] = useState("");
    const { brandID } = useParams();
    const guard = useOutletContext<PriceListAppOutletContext>();

    return (
        <>
            <Styled>
                <UI.HeroInputField>
                    <div>
                        <UI.HeroInput
                            autoFocus={!brandID}
                            onChange={setToken}
                            placeholder="Search"
                            value={token}
                        />
                        <button onClick={() => setToken("")} type="reset">
                            <IoClose />
                        </button>
                    </div>
                    <span>search Brands by name</span>
                </UI.HeroInputField>
                <Suspense fallback={<span>loading</span>}>
                    <BrandList token={token} guard={guard} />
                </Suspense>
            </Styled>
            <Outlet context={guard} />
        </>
    );
}

function BrandList({
    token,
    guard,
}: {
    token: string;
    guard: UnsavedChangesContext;
}) {
    const { brandID } = useParams();
    const navigate = useNavigate();

    const allBrands = useBrands();
    const brandsOfUser = useBrandsOfUser();

    const brands = allBrands.data.brands
        .filter(
            (brand) =>
                brandsOfUser.includes("*") || brandsOfUser.includes(brand.id),
        )
        .filter((brand) =>
            [
                brand.id === token,
                brand.name.toLowerCase().includes(token.toLowerCase()),
                brand.legacyAlias
                    ?.toLowerCase?.()
                    ?.includes?.(token.toLowerCase()),
                brand.legacyID === token,
            ].some(Boolean))
        .sort((a, b) => a.name.localeCompare(b.name));

    if (!brands.length) return <span>no brands</span>;

    async function handleSelect(id: string) {
        if (brandID === id) return;
        if (await confirmUnsavedChanges(guard)) navigate(id);
    }

    return (
        <ol>
            {brands.map((brand) => (
                <li key={brand.id}>
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleSelect(brand.id);
                        }}
                    >
                        {brand.name}
                    </a>
                </li>
            ))}
        </ol>
    );
}
